/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import centroid from '@turf/centroid';
import PermitsMap from 'shared-ui/maps/templates/PermitsMap';
import { facilityTypes, permitsIconTypes, permitSources } from 'shared-ui/maps/constants';
import { featureCollection } from 'shared-ui/maps/turfUtils';

function PermitMap(props) {
    const emptyCollection = featureCollection([]);
    const [pointData, setPointData] = useState(emptyCollection);
    const [pointsData, setPointsData] = useState(emptyCollection);
    const [linesData, setLinesData] = useState(emptyCollection);
    const [polygonsData, setPolygonsData] = useState(emptyCollection);
    const [selectedFeatures, setSelectedFeatures] = useState(emptyCollection);
    const [center, setCenter] = useState([-96, 39]);

    const evaluateFeature = (feat, stringFeatureId) => {
        let featId = feat.properties.id;
        if (!feat && typeof featId === 'undefined') return false;
        featId = typeof featId !== 'string' ? `${featId}` : featId;
        return featId === stringFeatureId;
    };

    const findFeature = (featureId) => {
        let foundFeature = null;
        if (!featureId) return foundFeature;

        const featId = typeof featureId !== 'string' ? `${featureId}` : featureId;
        const { geoJsonPoints, geoJsonLines, geoJsonPolygons } = props;

        if (geoJsonPoints?.features?.length && !foundFeature) {
            foundFeature = geoJsonPoints?.features.find((feat) =>
                evaluateFeature(feat, featId)
            );
        }

        if (geoJsonLines?.features?.length && !foundFeature) {
            foundFeature = geoJsonLines.features.find((feat) =>
                evaluateFeature(feat, featId)
            );
        }

        if (geoJsonPolygons?.features?.length && !foundFeature) {
            foundFeature = geoJsonPolygons.features.find((feat) =>
                evaluateFeature(feat, featId)
            );
        }

        return foundFeature;
    };

    useEffect(() => {
        if (!props.selectedFeatureId) return;
        const foundFeat = findFeature(props.selectedFeatureId);

        if (!isEqual(selectedFeatures, foundFeat) && props.fitFeatures) {
            setSelectedFeatures(featureCollection([foundFeat]));
        }
        if (!foundFeat) return;

        if (foundFeat.geometry?.type === 'Point') {
            setCenter(foundFeat.geometry.coordinates);
        } else {
            const centerFeat = centroid(foundFeat);
            if (centerFeat?.geometry) setCenter(centerFeat.geometry.coordinates);
        }
    }, [props.selectedFeatureId]);

    useEffect(() => {
        const hasGeoData =
            !!props.geoJsonLines?.features?.length ||
            !!props.geoJsonPoints?.features?.length ||
            !!props.geoJsonPolygons?.features?.length;

        const { geoJsonCenterPoint, center: centerCoords } = props;
        if (geoJsonCenterPoint && !hasGeoData) {
            setPointData(featureCollection([geoJsonCenterPoint]));
        } else if (hasGeoData) setPointData(featureCollection([]));

        const centerPoint = geoJsonCenterPoint?.geometry?.coordinates || centerCoords;
        if (centerPoint?.length && !isEqual(centerPoint, centerCoords)) {
            setCenter(centerPoint);
        }
    }, [props.geoJsonCenterPoint, props.center]);

    const processLines = (geojson) => {
        if (!geojson?.features?.length) return geojson;
        const processedGeojson = { ...geojson };

        processedGeojson.features = processedGeojson.features.map((feat) => {
            /* eslint-disable no-param-reassign */
            if (!feat?.properties?.color) feat.properties.color = '#345d96';
            /* eslint-enable no-param-reassign */
            return feat;
        });
        return processedGeojson;
    };

    const processPoints = (geojson) => {
        if (!geojson?.features?.length) return geojson;
        const processedGeojson = { ...geojson };

        processedGeojson.features = processedGeojson.features.map((feat) => {
            const { symbolType, type } = feat?.properties || {};
            const symbol = symbolType
                ? permitsIconTypes[symbolType] || permitsIconTypes.default
                : facilityTypes[type?.toLowerCase()]?.symbol ||
                  facilityTypes.default.symbol;

            /* eslint-disable no-param-reassign */
            feat.properties.symbol = symbol;
            /* eslint-enable no-param-reassign */

            return feat;
        });
        return processedGeojson;
    };

    const processPolygons = (geojson) => {
        if (!geojson?.features?.length) return geojson;
        const processedGeojson = { ...geojson };
        processedGeojson.features = processedGeojson.features
            .filter((feat) => feat?.properties?.visibility !== 'none')
            .map((feat) => {
                /* eslint-disable no-param-reassign */
                if (!feat.properties.color) feat.properties.color = '#fee4a0';
                if (!feat.properties.fillOpacity) feat.properties.fillOpacity = 0.33;
                /* eslint-enable no-param-reassign */
                return feat;
            });
        return processedGeojson;
    };

    useEffect(() => {
        setLinesData(processLines(props.geoJsonLines));
    }, [props.geoJsonLines]);
    useEffect(() => {
        setPointsData(processPoints(props.geoJsonPoints));
    }, [props.geoJsonPoints]);
    useEffect(() => {
        setPolygonsData(processPolygons(props.geoJsonPolygons));
    }, [props.geoJsonPolygons]);

    return (
        <PermitsMap
            sourceConfig={{
                point: { sourceName: permitSources.point, isClustered: false },
                points: { sourceName: permitSources.points, isClustered: false },
                lines: { sourceName: permitSources.lines, isClustered: false },
                polygons: { sourceName: permitSources.polygons, isClustered: false },
                highlightedLines: {
                    sourceName: permitSources.highlightedLines,
                    isClustered: false,
                },
            }}
            config={{
                mapBasicControls: { ignoreFullscreen: !props.fullscreenControl },
                layers: { ignoreLayer: !props.showPolygonLabels },
            }}
            featureName={props.geoJsonCenterPoint?.properties?.name}
            mapContainer={props.mapContainer}
            point={pointData}
            points={pointsData}
            lines={linesData}
            polygons={polygonsData}
            highlightedLines={selectedFeatures}
            center={center}
            zoom={17}
            service="permit"
            selectedEvent={props.handleClickCallback}
            onFullScreen={props.onFullScreen}
        />
    );
}

PermitMap.propTypes = {
    geoJsonPoints: PropTypes.object,
    geoJsonLines: PropTypes.object,
    geoJsonPolygons: PropTypes.object,
    geoJsonCenterPoint: PropTypes.object,
    handleClickCallback: PropTypes.func,
    selectedFeatureId: PropTypes.string,
    fullscreenControl: PropTypes.bool,
    center: PropTypes.array,
    mapContainer: PropTypes.string,
    showPolygonLabels: PropTypes.bool,
    featureName: PropTypes.string,
};

export default PermitMap;
